import { getPublicConfig } from "@/lib/config/public-config";
import { captureTelemetry } from "@/lib/diagnostics";
import {
  FLAGS_SETTLE_TIMEOUT_MS,
  markFlagsSettled,
} from "@/lib/analytics/flags-settled";
import type { BeforeSendFn, Properties } from "posthog-js";
import { previewFlagDisabled, previewFlagEnabled } from "@/lib/preview-flags";

type PostHogSdk = typeof import("posthog-js");

export const FEEDBACK_SURVEY_ID = "019ff919-b6c5-0000-569b-8e2c005ef71e";

let started = false;
let unreachable = false;
let sdk: PostHogSdk | undefined;
// Every caller below is synchronous and must not await the SDK chunk, so work
// raised before it lands waits here and replays in order once it resolves.
let pending: Array<(loaded: PostHogSdk) => void> = [];
const flagListeners = new Set<() => void>();

// Token routes carry a single-use secret in their last path segment. posthog-js
// stamps $current_url/$pathname/$referrer onto every event and $initial_* URLs
// into $set_once, so without redaction the live token would reach the analytics
// store where any project member could read it and take over the account or
// redeem the invitation.
const TOKEN_ROUTE = /\/(reset-password|invitations|verify-email)\/[^/?#]+/g;

function redactTokenRoutes(value: unknown): unknown {
  if (typeof value === "string")
    return value.replace(TOKEN_ROUTE, "/$1/:token");
  if (Array.isArray(value)) return value.map(redactTokenRoutes);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        redactTokenRoutes(entry),
      ]),
    );
  }
  return value;
}

// Returning null makes posthog-js drop the event, so this only ever rewrites.
const redactTokenRoutesBeforeSend: BeforeSendFn = (event) => {
  if (!event) return event;
  return {
    ...event,
    properties: redactTokenRoutes(event.properties) as Properties,
    ...(event.$set && { $set: redactTokenRoutes(event.$set) as Properties }),
    ...(event.$set_once && {
      $set_once: redactTokenRoutes(event.$set_once) as Properties,
    }),
  };
};

function withPostHog(run: (loaded: PostHogSdk) => void) {
  if (unreachable || !initializePostHog()) return;
  if (sdk) run(sdk);
  else pending.push(run);
}

export function initializePostHog() {
  if (started || typeof window === "undefined") return started;
  const config = getPublicConfig().posthog;
  if (!config?.enabled || !config.apiKey || !config.host) return false;
  const apiKey = config.apiKey;
  const host = config.host;
  const proxyHost = config.proxyHost;
  // Reported as initialized the moment capture is configured: the import below
  // only decides when events reach the network, not whether they are collected.
  started = true;
  setTimeout(markFlagsSettled, FLAGS_SETTLE_TIMEOUT_MS);
  // Dynamic so the SDK downloads after first paint instead of blocking it.
  void import("posthog-js")
    .then((loaded) => {
      loaded.default.init(apiKey, {
        // The reverse proxy exists to get past ad blockers, so prefer it for
        // ingestion when the deployment provides one.
        api_host: proxyHost ?? host,
        ui_host: host,
        person_profiles: "identified_only",
        capture_pageview: false,
        autocapture: false,
        before_send: redactTokenRoutesBeforeSend,
        // PostHog drops automated browsers before our synthetic marker is sent.
        // Permit those events so simulator coverage can be validated, while AUXM
        // continues to exclude synthetic=true from customer telemetry.
        opt_out_useragent_filter: navigator.webdriver === true,
      });
      loaded.default.onFeatureFlags(() => {
        markFlagsSettled();
        for (const listener of flagListeners) listener();
      });
      sdk = loaded;
      const queued = pending;
      pending = [];
      for (const run of queued) run(loaded);
    })
    .catch(() => {
      // An ad blocker can drop the chunk outright; without this the queue would
      // grow for the rest of the session behind an SDK that never arrives.
      unreachable = true;
      pending = [];
      markFlagsSettled();
    });
  return true;
}

// The browser cannot hold TELEMETRY_HASH_KEY, so the server hands it the same
// keyed hash it stores its own captures under. §10.1 forbids email in analytics
// payloads and §7.1 requires a keyed-hash identity; identifying by raw email (as
// distinct id and as properties) violated both. With no hash there is no
// identity to safely store against, so we skip rather than downgrade to email.
// No alias() from the old email id on purpose: linking the two would carry the
// raw-email history forward, so person histories fork at the switch.
export function identifyFeedbackUser(user: { analyticsId?: string | null }) {
  if (!user.analyticsId) return;
  const analyticsId = user.analyticsId;
  withPostHog((loaded) => loaded.default.identify(analyticsId));
}

export function capturePostHog(
  event: string,
  properties: Record<string, unknown>,
) {
  captureTelemetry(event, properties);
  withPostHog((loaded) => loaded.default.capture(event, properties));
}

export function capturePostHogPageview() {
  capturePostHog("$pageview", {
    $current_url: location.href,
    path: location.pathname,
    title: document.title,
    referrer: document.referrer,
  });
}

// No flag is known until the SDK has landed and answered, so callers get
// `fallback` for that window; subscribeToFeatureFlags is how they learn better.
export function featureFlagEnabled(key: string | undefined, fallback = true) {
  if (previewFlagEnabled(key)) return true;
  if (previewFlagDisabled(key)) return false;
  if (!key || !initializePostHog() || !sdk) return fallback;
  const value = sdk.default.getFeatureFlag(key);
  return value === undefined ? fallback : value !== false;
}

export function subscribeToFeatureFlags(listener: () => void) {
  if (!initializePostHog()) return () => undefined;
  flagListeners.add(listener);
  return () => {
    flagListeners.delete(listener);
  };
}

// The survey lives in a shadow root posthog-js owns, so the popover is found
// by scanning shadow hosts rather than by class on the document.
function findSurvey() {
  for (const element of document.querySelectorAll("*")) {
    const survey = element.shadowRoot?.querySelector(".ph-survey");
    if (survey) return survey;
  }
  return null;
}

let outsideDismissArmed = false;

// Clicking outside the survey dismisses it through PostHog's own cancel
// button, so the SDK still records the dismissal.
function armSurveyOutsideDismiss() {
  if (outsideDismissArmed) return;
  outsideDismissArmed = true;
  // The survey may not have rendered yet (the SDK chunk can still be in
  // flight), so its absence only means "closed" once it has been seen.
  let seen = false;
  const onMouseDown = (event: MouseEvent) => {
    const survey = findSurvey();
    if (!survey) {
      if (!seen) return;
      disarm();
      return;
    }
    seen = true;
    if (event.composedPath().includes(survey)) return;
    (survey.getRootNode() as ShadowRoot)
      .querySelector<HTMLElement>(".form-cancel")
      ?.click();
    disarm();
  };
  const disarm = () => {
    outsideDismissArmed = false;
    window.clearTimeout(renderDeadline);
    document.removeEventListener("mousedown", onMouseDown, true);
  };
  // displaySurvey can legitimately no-op (surveys disabled, ID not loaded);
  // if nothing has rendered by the deadline, drop the listener rather than
  // scanning the document on every mousedown for the rest of the session.
  const renderDeadline = window.setTimeout(() => {
    if (findSurvey()) seen = true;
    else if (!seen) disarm();
  }, 5000);
  document.addEventListener("mousedown", onMouseDown, true);
}

// True means the survey is on its way, not that it is already on screen: it may
// still be waiting on the SDK chunk.
export function showFeedbackSurvey() {
  if (!initializePostHog()) return false;
  withPostHog((loaded) => {
    loaded.default.displaySurvey(FEEDBACK_SURVEY_ID, {
      displayType: loaded.DisplaySurveyType.Popover,
      ignoreConditions: true,
      ignoreDelay: true,
    });
    armSurveyOutsideDismiss();
    const applyClientSurveyStyle = () => {
      for (const element of document.querySelectorAll("*")) {
        const root = element.shadowRoot;
        if (!root?.querySelector(".ph-survey")) continue;
        if (root.querySelector("#client-feedback-survey-style")) return true;
        const style = document.createElement("style");
        style.id = "client-feedback-survey-style";
        style.textContent = `
        .ph-survey {
          left: 50% !important;
          right: auto !important;
          top: 50% !important;
          bottom: auto !important;
          width: min(600px, 90vw) !important;
          max-width: 90vw !important;
          transform: translate(-50%, -50%) !important;
        }
        .ph-survey textarea,
        textarea.form-input {
          min-height: 240px !important;
          resize: vertical !important;
        }
      `;
        root.append(style);
        return true;
      }
      return false;
    };
    if (!applyClientSurveyStyle()) {
      const observer = new MutationObserver(() => {
        if (applyClientSurveyStyle()) observer.disconnect();
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      window.setTimeout(() => observer.disconnect(), 5000);
    }
  });
  return true;
}
